import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ACCEPTED_LEASE_EXTENSIONS,
  LEASE_STATUS,
  fetchLease,
  formatLeaseTimestamp,
  getLeaseDisplayStatus,
  isPdfUrl,
  sendLease,
  validateLeaseFile,
} from '../../../Services/leaseService';
import '../../../Components/Lease/LeaseAgreement.css';

const imageUrl = (path) => (window.getPublicUrl ? window.getPublicUrl(path) : path);

/**
 * Landlord-side lease agreement: once the tenant is approved, send the lease to
 * them for signature in a single call. The title and document are both optional.
 */
const LeaseAgreementCard = ({ propertyId, tenantId, tenantName, onViewDocument }) => {
  const fileInputRef = useRef(null);
  const [lease, setLease] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isSending, setIsSending] = useState(false);

  const loadLease = useCallback(async ({ silent = false } = {}) => {
    if (!propertyId || !tenantId) {
      setIsLoading(false);
      return;
    }
    if (!silent) setIsLoading(true);
    try {
      const row = await fetchLease({ propertyId, tenantId });
      setLease(row);
    } catch (error) {
      const statusCode = error?.response?.status ?? error?.response?.data?.statusCode;
      if (statusCode === 404) {
        setLease(null);
      } else {
        console.error('Error loading lease agreement:', error);
        if (!silent) {
          toast.error(error?.response?.data?.message || 'Failed to load lease agreement.');
        }
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [propertyId, tenantId]);

  useEffect(() => {
    loadLease();
  }, [loadLease]);

  const resetForm = () => {
    setTitle('');
    setFile(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const picked = event.target.files?.[0] || null;
    if (!picked) {
      setFile(null);
      return;
    }
    const error = validateLeaseFile(picked);
    if (error) {
      setFileError(error);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError('');
    setFile(picked);
  };

  const handleSend = async () => {
    if (!propertyId || !tenantId) {
      toast.error('Missing property or tenant information for the lease.');
      return;
    }
    const trimmedTitle = title.trim();
    const fileValidationError = file ? validateLeaseFile(file) : null;
    if (fileValidationError) {
      setFileError(fileValidationError);
      return;
    }

    // Browsers only allow a new tab straight from the click, not after the API call
    // resolves — so open it now and point it at the prepare page once we have the URL.
    const prepareTab = window.open('', '_blank');
    if (prepareTab) {
      prepareTab.document.title = 'Preparing lease…';
      prepareTab.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 24px; color: #444;">Preparing your lease…</p>';
    }

    setIsSending(true);
    try {
      const result = await sendLease({ propertyId, tenantId, title: trimmedTitle, file });
      const sentFileName = file?.name;
      resetForm();
      setIsFormOpen(false);
      // Fall back to a local record when the API returns no lease row, so the
      // landlord still sees what was just uploaded.
      const nextLease = result?.lease ?? {
        id: null,
        documentId: null,
        title: trimmedTitle || 'Lease Agreement',
        documentUrl: null,
        sendUrl: null,
        requiresFieldPlacement: false,
        fileName: sentFileName,
        status: LEASE_STATUS.SENT,
        sentAt: Date.now(),
        signedAt: null,
        signedBy: null,
        signedDocumentUrl: null,
        declineReason: null,
      };
      if (!nextLease.fileName) nextLease.fileName = sentFileName;
      setLease(nextLease);

      // Not delivered yet when the provider wants signature fields placed first —
      // take the landlord straight to the prepare page.
      if (nextLease.requiresFieldPlacement && nextLease.sendUrl) {
        toast.info(result?.message || 'Place the signature fields in the new tab, then send the lease.');
        if (prepareTab && !prepareTab.closed) {
          prepareTab.opener = null;
          prepareTab.location.href = nextLease.sendUrl;
        } else {
          window.open(nextLease.sendUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        prepareTab?.close();
        toast.success(result?.message || 'Lease sent to the tenant for signature.');
      }
    } catch (error) {
      prepareTab?.close();
      console.error('Error sending lease agreement:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send lease agreement.');
    } finally {
      setIsSending(false);
    }
  };

  // The lease is sent from the provider's prepare page in another tab. Refresh the
  // status whenever the landlord comes back to this tab so the card moves on.
  const awaitingPrepare = !!(lease?.requiresFieldPlacement && lease?.sendUrl);
  useEffect(() => {
    if (!awaitingPrepare) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'visible') loadLease({ silent: true });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [awaitingPrepare, loadLease]);

  const handleView = () => {
    const url = lease?.signedDocumentUrl || lease?.documentUrl;
    if (!url) return;
    // The shared viewer only renders PDFs and images inline; Word files download instead.
    if (onViewDocument && isPdfUrl(url)) {
      onViewDocument({ type: lease.title || 'Lease Agreement', imageUrl: url, allUrls: [url], isPdf: true });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const statusInfo = lease ? getLeaseDisplayStatus(lease) : null;
  const isSigned = !lease?.requiresFieldPlacement && statusInfo?.key === LEASE_STATUS.SIGNED;
  const isDeclined = !lease?.requiresFieldPlacement && statusInfo?.key === LEASE_STATUS.DECLINED;
  const needsFieldPlacement = awaitingPrepare;
  const showForm = !lease || isFormOpen;

  const openPrepareInNewTab = () => {
    if (lease?.sendUrl) window.open(lease.sendUrl, '_blank', 'noopener,noreferrer');
  };

  const detailRows = [];
  if (lease?.sentAt) {
    detailRows.push({
      id: 'sent',
      label: needsFieldPlacement ? 'Uploaded on' : 'Sent to tenant',
      value: formatLeaseTimestamp(lease.sentAt),
    });
  }
  if (lease?.signedAt) {
    detailRows.push({ id: 'signed', label: 'Signed on', value: formatLeaseTimestamp(lease.signedAt) });
  }
  if (lease?.signedBy) {
    detailRows.push({ id: 'signedBy', label: 'Signed by', value: String(lease.signedBy) });
  }

  return (
    <div className="tenant-detail-card">
      <div className="tenant-detail-card-header">
        <img src={imageUrl('/assets/images/file-04.png')} alt="Lease" className="tenant-detail-card-icon" />
        <h3 className="tenant-detail-card-title">Lease Agreement</h3>
      </div>
      <div className="lease-card-body">
        {isLoading ? (
          <p className="lease-empty-text">Loading lease agreement…</p>
        ) : (
          <>
            {lease ? (
              <>
                <div className="lease-document-item">
                  <div className="lease-document-info">
                    <span className="lease-document-title">{lease.title || 'Lease Agreement'}</span>
                    {lease.fileName ? <span className="lease-document-meta">{lease.fileName}</span> : null}
                  </div>
                  <div className="lease-document-actions">
                    <span className={`lease-badge lease-badge--${statusInfo.variant}`}>
                      {statusInfo.label}
                    </span>
                    {lease.documentUrl || lease.signedDocumentUrl ? (
                      <button
                        type="button"
                        className="tenant-detail-action-icon"
                        onClick={handleView}
                        aria-label="View lease document"
                      >
                        <img src={imageUrl('/assets/images/eye-open_new.png')} alt="View" />
                      </button>
                    ) : null}
                  </div>
                </div>

                {detailRows.length > 0 ? (
                  <dl className="lease-detail-grid">
                    {detailRows.map((row) => (
                      <div key={row.id} className="lease-detail-item">
                        <dt className="lease-detail-label">{row.label}</dt>
                        <dd className="lease-detail-value">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}

                {isDeclined && lease.declineReason ? (
                  <p className="lease-note lease-note--declined">Tenant declined: {lease.declineReason}</p>
                ) : null}

                {needsFieldPlacement ? (
                  <p className="lease-note">
                    This lease has not reached {tenantName || 'the tenant'} yet. Place the signature fields
                    on the prepare page (opened in a new tab), then send it from there.
                  </p>
                ) : isSigned ? (
                  <p className="lease-note">
                    {tenantName ? `${tenantName} has` : 'The tenant has'} signed this lease.
                  </p>
                ) : (
                  <p className="lease-note">
                    {tenantName ? `${tenantName} has` : 'The tenant has'} been notified and can sign the
                    lease from their property page.
                  </p>
                )}

                {!showForm && !isSigned ? (
                  <div className="lease-actions">
                    {needsFieldPlacement ? (
                      <button
                        type="button"
                        className="lease-btn lease-btn--primary"
                        onClick={openPrepareInNewTab}
                      >
                        Prepare &amp; Send
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="lease-btn lease-btn--secondary"
                      onClick={() => setIsFormOpen(true)}
                    >
                      Send a Different Lease
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="lease-empty-text">
                No lease sent yet. Send the lease to the tenant for signature — adding a title or document is optional.
              </p>
            )}

            {showForm ? (
              <>
                <div className="lease-field">
                  <label className="lease-label" htmlFor="lease-title-input">
                    Lease title <span className="lease-label-optional">(optional)</span>
                  </label>
                  <input
                    id="lease-title-input"
                    type="text"
                    className="lease-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. 12-Month Residential Lease – 123 Oak Street"
                    maxLength={150}
                  />
                </div>

                <div className="lease-field">
                  <span className="lease-label">
                    Lease document <span className="lease-label-optional">(optional)</span>
                  </span>
                  <div className="lease-file-row">
                    <button
                      type="button"
                      className="lease-file-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSending}
                    >
                      Choose File
                    </button>
                    <span className={`lease-file-name${file ? '' : ' lease-file-name--empty'}`}>
                      {file ? file.name : 'No file selected'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_LEASE_EXTENSIONS}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {fileError ? (
                    <p className="lease-field-error">{fileError}</p>
                  ) : (
                    <p className="lease-hint">PDF, DOC or DOCX — up to 10 MB.</p>
                  )}
                </div>

                <div className="lease-actions">
                  <button
                    type="button"
                    className="lease-btn lease-btn--primary"
                    onClick={handleSend}
                    disabled={isSending}
                  >
                    {isSending ? 'Sending…' : 'Send to Tenant'}
                  </button>
                  {lease || file || title ? (
                    <button
                      type="button"
                      className="lease-btn lease-btn--secondary"
                      onClick={() => {
                        resetForm();
                        if (lease) setIsFormOpen(false);
                      }}
                      disabled={isSending}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>

    </div>
  );
};

export default LeaseAgreementCard;
